import { CommandBar, DetailsList, Icon, Panel, PanelType, SelectionMode, TooltipHost, MessageBar, MessageBarType, Selection, Spinner, IconButton, Stack, PrimaryButton, Label } from "office-ui-fabric-react";
import { useCallback, useEffect, useState } from "react";
import { cancelBooking, getMyBookings } from "../../../api/bookings";
import { IBooking, IMyBooking } from "../../../types/Booking.types";
import { DeskBookingForm } from "../forms/DeskBookingForm";
import { RoomBookingForm } from "../forms/RoomBookingForm";
import { BookingList } from "./MyBookingList";

import styles from "./MyBookings.module.scss";

export interface IMyBookingsPanelProps {
    onDismiss: () => void;
}

export const MyBookingsPanel = (props: IMyBookingsPanelProps) => {
    const { onDismiss } = props;

    const [ cancelling, setCancelling ] = useState(false);
    const [ bookings, setBookings ] = useState<IMyBooking[]>();
    const [ bookingSelected, setBookingSelected ] = useState<IMyBooking|undefined>();
    const [ viewDeskBooking, setViewDeskBooking ] = useState<IMyBooking|undefined>();
    const [ viewRoomBooking, setViewRoomBooking ] = useState<IMyBooking|undefined>();

    const onSelectionChanged = useCallback(function (table: string) {
        return function (this: Selection) {
            const selectedItems = this.getSelection() as IMyBooking[];
            if (selectedItems !== undefined && selectedItems.length > 0) {
                setBookingSelected(selectedItems[0] as IMyBooking);
            } else {
                setBookingSelected(undefined);
            }
            if (table === "approvedBookings") {
                selectionPending.setAllSelected(false);
            } else {
                selection.setAllSelected(false);
            }
        };
    }, []);
    
    const [ selection ] = useState(new Selection({ onSelectionChanged: onSelectionChanged("approvedBookings") }));
    const [ selectionPending ] = useState(new Selection({ onSelectionChanged: onSelectionChanged("pendingBookings") }));

    const _load = async () => {
        const response = await getMyBookings();
        if (response.ok) {
            const bookings = await response.json() as IMyBooking[];
            setBookings(bookings);
        }
    }

    const _cancelBooking = async (booking: IMyBooking) => {
        setCancelling(true);
        booking.CancelledByAdmin = false;
        const response = await cancelBooking(booking);
        if (response.ok) {
            setCancelling(false);
            _load();
        } else {
            alert('Unable to cancel booking');
            setCancelling(false);
        }
        selection.setAllSelected(false);
        selectionPending.setAllSelected(false);
    }

    useEffect(() => {
        _load();
    }, []);

    return (
        <Panel type={PanelType.custom} customWidth="900px" isOpen={true} headerText="My Bookings" onDismiss={onDismiss}>
            <p>Bookings that you make - for you and on behalf of others - will appear here:</p>
            { window.innerWidth < 640 && (
                <MessageBar messageBarType={MessageBarType.info}>You can scroll the table below with the mouse or your finger</MessageBar>
            )}
            <CommandBar
                className={styles.commandBar}
                items={[
                    {
                        disabled: !bookingSelected,
                        key: "view",
                        text: window.innerWidth < 640 ? "View" : "View Booking",
                        onClick: () => {
                            if (bookingSelected) {
                                if (bookingSelected.DeskId) {
                                    setViewDeskBooking(bookingSelected);
                                } else {
                                    setViewRoomBooking(bookingSelected);
                                }
                            }
                        }
                    },
                    {
                        disabled: !bookingSelected || cancelling,
                        key: "cancel",
                        text: window.innerWidth < 640 ? "Cancel" : "Cancel Booking",
                        onRender: cancelling ? () => <Spinner label="Cancelling..." /> : undefined,
                        onClick: () => {
                            if (bookingSelected) {
                                _cancelBooking(bookingSelected);
                            }
                        }
                    }
                ]}
            />
            { bookings !== undefined && bookings.length > 0 && (
                <>
                    <BookingList
                        label="Approved Bookings"
                        listKey="approved"
                        selection={selection}
                        bookings={bookings.filter((b) => Boolean(b.DeskId) || (Boolean(b.RoomId) && b.Status === 1))}
                    />
                    { bookings.filter((b) => (Boolean(b.RoomId) && b.Status !== 1)).length > 0 && (
                        <>
                            <BookingList
                                label="Room Booking Requests (Pending / Rejected)"
                                listKey="pending"
                                selection={selectionPending}
                                bookings={bookings.filter((b) => (Boolean(b.RoomId) && b.Status !== 1))}
                                tooltip="These bookings require approval"
                                showStatus={true}
                            />
                        </>
                    )}
                    <Stack horizontal horizontalAlign="end" style={{marginTop: 20}}>
                        <PrimaryButton text="Export" iconProps={{iconName: "Export"}}
                            href={"/api/bookings/my/export"} target="_blank" />
                    </Stack>
                </>
            )}
            { viewDeskBooking !== undefined && (
                <DeskBookingForm startDateStr={viewDeskBooking.Start} endDateStr={viewDeskBooking.Finish}
                    floorplanId={viewDeskBooking.FloorplanId} locationId={viewDeskBooking.LayoutId}
                    onDismiss={() => setViewDeskBooking(undefined)}
                    booking={viewDeskBooking}
                    readOnly={true}
                    postCode={viewDeskBooking.PostCode}
                    />
            )}
            { viewRoomBooking !== undefined && (
                <RoomBookingForm
                    finish={new Date(viewRoomBooking.Finish)}
                    onDismiss={(changesMade) => {
                        setViewRoomBooking(undefined);
                        if (changesMade) {
                            _load();
                        }
                    }}
                    booking={viewRoomBooking}
                    roomId={viewRoomBooking.RoomId as number}
                    start={new Date(viewRoomBooking.Start)}
                />
            )}
        </Panel>    
    );
}